import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Settings2, Check } from "lucide-react";
import { Button } from "../..";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../command";

export interface ColumnOption {
  key: string;
  label: string;
  width?: string;
}

export interface ColumnSelectorProps {
  columns: ColumnOption[];
  visibleColumns: Record<string, boolean>;
  onToggleColumn: (columnKey: string) => void;
  buttonText?: string;
  className?: string;
  disabled?: boolean;
  align?: "left" | "right";
}

export function ColumnSelector({
  columns,
  visibleColumns,
  onToggleColumn,
  buttonText = "View",
  className = "",
  disabled = false,
  align = "left",
}: ColumnSelectorProps) {
  const [open, setOpen] = useState(false);

  const handleSelect = (columnKey: string) => {
    onToggleColumn(columnKey);
  };

  return (
    <div className={className}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <Button variant="outline" size="sm" className="flex items-center gap-2" disabled={disabled}>
            <Settings2 className="h-4 w-4" />
            {buttonText}
          </Button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className="w-[280px] rounded-md border bg-popover p-0 shadow-md animate-in fade-in-80 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
            align={align === "right" ? "end" : "start"}
            sideOffset={4}
          >
            <Command className="w-full">
              <CommandInput placeholder="Search columns..." />
              <CommandList className="max-h-[300px]">
                <CommandEmpty>No columns found.</CommandEmpty>
                <CommandGroup>
                  {columns.map((column) => (
                    <CommandItem
                      key={column.key}
                      value={column.label}
                      onSelect={() => handleSelect(column.key)}
                      className="flex items-center gap-2"
                    >
                      <span className="flex-1 truncate min-w-0">{column.label}</span>
                      <span className="flex-shrink-0 w-4">
                        {visibleColumns[column.key] && <Check className="h-4 w-4" />}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
